import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/siteData';

export default function HeroSection() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <section className="relative w-full min-h-[620px] md:min-h-[700px] flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Elegant%20product%20showcase%20display%20with%20carefully%20arranged%20sneakers%20hoodies%20bags%20and%20accessories%20on%20a%20warm%20cream%20pedestal%2C%20soft%20natural%20diffused%20lighting%20from%20above%2C%20clean%20minimalist%20retail%20display%20concept%20with%20subtle%20beige%20and%20warm%20white%20tones%2C%20organized%20fashion%20items%20composition%2C%20editorial%20product%20photography%20with%20gentle%20shadows%20and%20sophisticated%20modern%20boutique%20atmosphere%2C%20ample%20copy%20space%20on%20the%20upper%20portion%20for%20text%20overlay&width=1600&height=900&seq=hero-bg-v2&orientation=landscape"
          alt="Sugargoo Spreadsheet fashion product showcase with sneakers and accessories arranged on a minimalist display"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/25"></div>
      </div>

      <div className="relative z-10 w-full max-w-[1200px] mx-auto px-4 md:px-6 py-20 md:py-24">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          <div className="flex-1 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-50/80 backdrop-blur-sm border border-background-200/50 mb-6">
              <span className="w-2 h-2 rounded-full bg-primary-500 animate-pulse"></span>
              <span className="text-xs font-medium text-foreground-700 whitespace-nowrap">Updated Daily Resources</span>
            </div>

            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight mb-6">
              Sugargoo Spreadsheet:<br />
              <span className="text-accent-400">Ultimate Guide</span> To Products,<br />
              Reviews &amp; Shopping
            </h1>

            <p className="text-base md:text-lg text-background-50/80 max-w-xl mx-auto lg:mx-0 mb-8 leading-relaxed">
              Find updated Sugargoo Spreadsheet resources, product recommendations, shipping information, warehouse guides, discount opportunities and expert shopping tutorials in one trusted platform.
            </p>

            <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full sm:w-auto px-8 py-3.5 bg-accent-500 text-background-50 font-semibold rounded-lg hover:bg-accent-600 transition-all duration-300 text-sm whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2"
              >
                sugargoo spreadsheet
                <i className="ri-arrow-right-line"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full sm:w-auto px-8 py-3.5 bg-background-50/15 backdrop-blur-sm text-background-50 font-semibold rounded-lg border border-background-50/25 hover:bg-background-50/25 transition-all duration-300 text-sm whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2"
              >
                buy it now
                <i className="ri-external-link-line"></i>
              </a>
            </div>

            <div className="flex flex-wrap items-center gap-4 md:gap-6 justify-center lg:justify-start mt-8 text-xs text-background-50/70">
              <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                <i className="ri-check-line text-accent-400"></i> Updated Daily
              </span>
              <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                <i className="ri-check-line text-accent-400"></i> Beginner Friendly
              </span>
              <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                <i className="ri-check-line text-accent-400"></i> Independent Research
              </span>
              <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                <i className="ri-check-line text-accent-400"></i> Shopping Safety Info
              </span>
            </div>
          </div>

          <div className="flex-shrink-0 w-full max-w-[400px] lg:max-w-[440px]">
            <div className="bg-background-50 rounded-2xl p-6 border border-background-200/60">
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-1">Browse Categories</h3>
              <p className="text-sm text-foreground-500 mb-4">Quick access to all product categories on our main site</p>

              <div ref={dropdownRef} className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-background-100 rounded-lg border border-background-200/60 text-sm font-medium text-foreground-700 hover:border-background-300/60 transition-colors cursor-pointer"
                >
                  <span>Select a category...</span>
                  <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-background-50 rounded-xl border border-background-200/60 shadow-sm max-h-[340px] overflow-y-auto z-20 transition-all duration-150 ease-out">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center justify-between px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer border-b border-background-100 last:border-b-0 whitespace-nowrap"
                      >
                        <span>{cat.name}</span>
                        <i className="ri-arrow-right-s-line text-foreground-400"></i>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-5">
                <Link
                  to="/article/sugargoo-spreadsheet-guide"
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-semibold rounded-lg hover:bg-primary-600 transition-colors text-sm whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-article-line"></i>
                  Read Complete Guide
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}